import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';

const MortgageCalculator = () => {
  const [homeValue, setHomeValue] = useState<number>(300000);
  const [downPayment, setDownPayment] = useState<number>(60000);
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(20);
  const [interestRate, setInterestRate] = useState<number>(4.5);
  const [loanTerm, setLoanTerm] = useState<number>(30);
  const [paymentFrequency, setPaymentFrequency] = useState<string>("monthly");
  
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [totalInterest, setTotalInterest] = useState<number>(0);
  
  // Add ref to the calculator container
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Add effect to prevent scrolling on focus
  useEffect(() => {
    // Function to prevent scroll on focus
    const preventScrollOnFocus = (e: Event) => {
      e.preventDefault();
      // Keep focus but prevent scrolling
      if (calculatorRef.current) {
        calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Get all focusable elements within the calculator
    const focusableElements = calculatorRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // Add event listeners to prevent scroll
    focusableElements?.forEach((el) => {
      el.addEventListener('focus', preventScrollOnFocus);
    });

    // Clean up
    return () => {
      focusableElements?.forEach((el) => {
        el.removeEventListener('focus', preventScrollOnFocus);
      });
    };
  }, [calculatorRef]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Calculate mortgage when inputs change
  useEffect(() => {
    calculateMortgage();
  }, [homeValue, downPayment, interestRate, loanTerm, paymentFrequency]);

  // Handle home value change
  const handleHomeValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setHomeValue(value);
    
    // Update down payment amount while keeping percentage the same
    const newDownPayment = (value * downPaymentPercent) / 100;
    setDownPayment(newDownPayment);
  };

  // Handle down payment amount change
  const handleDownPaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setDownPayment(value);
    
    // Update down payment percentage
    const newPercentage = (value / homeValue) * 100;
    setDownPaymentPercent(parseFloat(newPercentage.toFixed(2)));
  };

  // Handle down payment percentage change
  const handleDownPaymentPercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setDownPaymentPercent(value);
    
    // Update down payment amount
    const newDownPayment = (homeValue * value) / 100;
    setDownPayment(parseFloat(newDownPayment.toFixed(2)));
  };

  // Calculate mortgage payment
  const calculateMortgage = () => {
    try {
      // Calculate loan amount
      const loanAmount = homeValue - downPayment;
      
      if (loanAmount <= 0 || interestRate <= 0 || loanTerm <= 0) {
        setMonthlyPayment(0);
        setTotalPayments(0);
        setTotalInterest(0);
        return;
      }
      
      // Convert annual interest rate to monthly
      const monthlyRate = interestRate / 100 / 12;
      
      // Calculate number of payments
      let numberOfPayments = loanTerm * 12;
      
      // Adjust for payment frequency
      let paymentMultiplier = 1;
      if (paymentFrequency === "bi-weekly") {
        numberOfPayments = loanTerm * 26;
        paymentMultiplier = 12 / 26;
      } else if (paymentFrequency === "weekly") {
        numberOfPayments = loanTerm * 52;
        paymentMultiplier = 12 / 52;
      }
      
      // Calculate monthly payment using the formula: P = L[c(1 + c)^n]/[(1 + c)^n - 1]
      const payment = loanAmount * 
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      
      // Adjust payment for frequency
      const adjustedPayment = payment * paymentMultiplier;
      
      setMonthlyPayment(adjustedPayment);
      setTotalPayments(adjustedPayment * numberOfPayments);
      setTotalInterest(adjustedPayment * numberOfPayments - loanAmount);
      
      toast({
        title: "Mortgage calculated",
        description: `Your ${paymentFrequency} payment will be ${formatCurrency(adjustedPayment)}`,
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "Please check your inputs and try again",
        variant: "destructive"
      });
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Get payment frequency label
  const getPaymentLabel = () => {
    switch(paymentFrequency) {
      case "weekly": return "Weekly";
      case "bi-weekly": return "Bi-Weekly";
      case "monthly": default: return "Monthly";
    }
  };

  return (
    <div className="space-y-6" ref={calculatorRef}>
      <Tabs defaultValue="calculator">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
          <TabsTrigger value="amortization">Amortization</TabsTrigger>
        </TabsList>
        
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Input Section */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="homeValue">Home Value</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="homeValue"
                        type="number"
                        min="0"
                        step="1000"
                        value={homeValue}
                        onChange={handleHomeValueChange}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="downPayment">Down Payment</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          id="downPayment"
                          type="number"
                          min="0"
                          step="1000"
                          value={downPayment}
                          onChange={handleDownPaymentChange}
                          className="pl-8"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="downPaymentPercent">Down Payment %</Label>
                      <div className="relative">
                        <Input
                          id="downPaymentPercent"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={downPaymentPercent}
                          onChange={handleDownPaymentPercentChange}
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="interestRate">Interest Rate</Label>
                    <div className="relative">
                      <Input
                        id="interestRate"
                        type="number"
                        min="0"
                        max="20"
                        step="0.01"
                        value={interestRate}
                        onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="loanTerm">Loan Term (Years)</Label>
                    <Input
                      id="loanTerm"
                      type="number"
                      min="1"
                      max="40"
                      step="1"
                      value={loanTerm}
                      onChange={(e) => setLoanTerm(parseInt(e.target.value) || 0)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="paymentFrequency">Payment Frequency</Label>
                    <Select
                      value={paymentFrequency}
                      onValueChange={setPaymentFrequency}
                    >
                      <SelectTrigger id="paymentFrequency">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Results Section */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-4">Mortgage Summary</h3>
                
                <div className="space-y-4">
                  <div className="p-4 bg-primary/10 rounded-md">
                    <div className="text-sm text-muted-foreground">{getPaymentLabel()} Payment</div>
                    <div className="text-2xl font-bold">{formatCurrency(monthlyPayment)}</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Loan Amount</div>
                      <div className="text-lg font-medium">{formatCurrency(homeValue - downPayment)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Interest</div>
                      <div className="text-lg font-medium">{formatCurrency(totalInterest)}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Total Payments</div>
                      <div className="text-lg font-medium">{formatCurrency(totalPayments)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Cost</div>
                      <div className="text-lg font-medium">{formatCurrency(homeValue)}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="amortization">
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-4">Amortization Schedule</h3>
              <p className="text-muted-foreground mb-4">
                This shows how your payments are applied to the principal and interest over time.
              </p>
              
              {/* Placeholder for amortization table - would be implemented with actual data */}
              <div className="text-center p-8 border rounded-md">
                <p className="text-muted-foreground">
                  Amortization schedule will be displayed here.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MortgageCalculator;
